// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/shortcuts/platform.ts
// Parameters       : none — below this header, identical across the fleet
// Destination      : packages/fleet/shortcuts/src/platform.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo field-check-app
//     node fleet/fleet.ts check --repo field-check-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
/* oxlint-disable lingui/no-unlocalized-strings -- TODO(i18n): traducir nombres de teclas/plataformas (deuda) */
/**
 * Platform adapter for the shortcut system.
 *
 * Decides:
 *   - what "Mod" means (Cmd on macOS, Ctrl elsewhere)
 *   - which key strings the host environment reserves
 *   - how a binding should render in <kbd> badges (⌘, ⇧, ⌥, Ctrl, Alt, Shift…)
 *   - how to translate a KeyboardEvent into the engine's canonical signature
 */

import { eventToSignature } from "./keys";
import type { Platform, PlatformAdapter } from "./types";

function isTauriRuntime(): boolean {
  return typeof globalThis !== "undefined" && "__TAURI_INTERNALS__" in globalThis;
}

function readUserAgentDataPlatform(): string | undefined {
  if (typeof navigator === "undefined") {
    return undefined;
  }
  const data: unknown = Reflect.get(navigator, "userAgentData");
  if (data && typeof data === "object") {
    const platform: unknown = Reflect.get(data, "platform");
    if (typeof platform === "string") {
      return platform;
    }
  }
  return undefined;
}

function readChromeRuntimeId(): string | undefined {
  if (typeof globalThis === "undefined") {
    return undefined;
  }
  const chrome: unknown = Reflect.get(globalThis, "chrome");
  if (!chrome || typeof chrome !== "object") {
    return undefined;
  }
  const runtime: unknown = Reflect.get(chrome, "runtime");
  if (!runtime || typeof runtime !== "object") {
    return undefined;
  }
  const id: unknown = Reflect.get(runtime, "id");
  return typeof id === "string" ? id : undefined;
}

function detectMac(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }
  const uaDataPlatform = readUserAgentDataPlatform();
  if (uaDataPlatform) {
    return /mac/iu.test(uaDataPlatform);
  }
  return /Mac|iPod|iPhone|iPad/u.test(navigator.userAgent);
}

function isExtensionContext(): boolean {
  return Boolean(readChromeRuntimeId());
}

function isMobileTauri(): boolean {
  if (!isTauriRuntime()) {
    return false;
  }
  return typeof navigator !== "undefined" && /Android|iPhone|iPad|iPod/u.test(navigator.userAgent);
}

export function detectPlatform(): Platform {
  if (isExtensionContext()) {
    return "extension";
  }
  if (isTauriRuntime()) {
    return isMobileTauri() ? "tauri-mobile" : "tauri-desktop";
  }
  return "web";
}

const MAC_KEY_GLYPHS: Record<string, string> = {
  Mod: "⌘",
  Meta: "⌘",
  Cmd: "⌘",
  Ctrl: "⌃",
  Control: "⌃",
  Alt: "⌥",
  Option: "⌥",
  Shift: "⇧",
  Enter: "↵",
  ArrowUp: "↑",
  ArrowDown: "↓",
  ArrowLeft: "←",
  ArrowRight: "→",
  Backspace: "⌫",
  Delete: "⌦",
  Escape: "Esc",
  Tab: "Tab",
  Space: "Space",
};

const PC_KEY_GLYPHS: Record<string, string> = {
  Mod: "Ctrl",
  Meta: "Win",
  Cmd: "Ctrl",
  Ctrl: "Ctrl",
  Control: "Ctrl",
  Alt: "Alt",
  Option: "Alt",
  Shift: "Shift",
  Enter: "Enter",
  ArrowUp: "↑",
  ArrowDown: "↓",
  ArrowLeft: "←",
  ArrowRight: "→",
  Backspace: "Backspace",
  Delete: "Delete",
  Escape: "Esc",
  Tab: "Tab",
  Space: "Space",
};

/**
 * Browser-reserved bindings that web pages should not attempt to capture.
 *
 * In web context we treat these as dead; in Tauri desktop we have full freedom
 * to intercept them.
 */
const WEB_RESERVED_BINDINGS = new Set<string>([
  "Mod+N", // new window
  "Mod+Shift+N", // incognito
  "Mod+T", // new tab
  "Mod+Shift+T", // reopen tab
  "Mod+W", // close tab
  "Mod+Q", // quit browser
  "Mod+R", // reload
  "Mod+Shift+R", // hard reload
  "F5", // reload
  "Ctrl+F5", // hard reload
  "Mod+L", // address bar
  "Mod+D", // bookmark
  "Mod+J", // downloads
  "Mod+H", // history
  "Mod+Shift+I", // devtools
  "Mod+Alt+I", // devtools (Mac)
  "F12", // devtools
]);

export function createPlatformAdapter(
  platformId: Platform = detectPlatform(),
  isMac: boolean = detectMac(),
): PlatformAdapter {
  const glyphs = isMac ? MAC_KEY_GLYPHS : PC_KEY_GLYPHS;
  const isWeb = platformId === "web" || platformId === "extension";

  return {
    id: platformId,
    isMac,
    reservedBindings: isWeb ? WEB_RESERVED_BINDINGS : new Set(),
    formatKey(token: string): string {
      const match = glyphs[token];
      if (match !== undefined) {
        return match;
      }
      if (token.length === 1) {
        return token.toUpperCase();
      }
      return token;
    },
    normalizeEvent(event: KeyboardEvent): string {
      const sig = eventToSignature(event, isMac);
      const parts: string[] = [];
      if (sig.mod) {
        parts.push("Mod");
      }
      if (sig.ctrl) {
        parts.push("Ctrl");
      }
      if (sig.alt) {
        parts.push("Alt");
      }
      if (sig.shift) {
        parts.push("Shift");
      }
      parts.push(sig.key);
      return parts.join("+");
    },
  };
}
